import type { RouteObject } from "react-router";
import { NotFoundScene } from "~/components/NotFoundScene";
import { isDestinationHidden } from "./hiddenDestinations";

/**
 * Closes the project pages of the products this deployment hides: the route
 * stays in the table, so the address still matches, but it renders not found
 * instead of loading the page. The menus, Quick Search and the home checklist
 * already leave these pages out (`isDestinationHidden`); this is what keeps
 * an address typed or bookmarked from opening them.
 *
 * Only project routes (`/:project/...`) are closed here. Settings pages guard
 * themselves with `withSurfaceGuard`, and the Security page stays reachable
 * by address on purpose.
 *
 * Spec: specs/branding/product-surfaces.feature
 */
export function withHiddenProjectRoutesClosed(
  routes: RouteObject[],
): RouteObject[] {
  return routes.map((route) => {
    const children = route.children
      ? withHiddenProjectRoutesClosed(route.children)
      : undefined;
    if (
      route.path?.startsWith("/:project/") &&
      isDestinationHidden(route.path)
    ) {
      const { lazy: _lazy, element: _element, ...rest } = route;
      return { ...rest, Component: NotFoundScene, children } as RouteObject;
    }
    return children ? ({ ...route, children } as RouteObject) : route;
  });
}
